import API from '@/services/API'
import { GraphQLResult } from '@aws-amplify/api-graphql'
import { PlatformSurvey } from '@/types/Platform'
import { PlatformSurveyGroup, QuestionnaireListItem, SurveyGroupProgress } from '@/types/SurveyGroup'
import { GetFullSurveyGroupQuery, ListFullSurveyGroupsByQuestionnaireQuery } from '@/API'
import { getFullSurveyGroup } from '@/graphql/custom/getFullSurveyGroup'
import { listFullSurveyGroupsByQuestionnaire } from '@/graphql/custom/listFullSurveyGroupsByQuestionnaire'
import { calculateCompletion } from '@/helpers/survey'
import { PlatformQuestionnaire } from '@betterboards/shared/types/Platform'
import { getDisplayComparisonDate } from '@betterboards/shared/helpers/util/getDisplayComparisonDate'

export function calculateSurveyGroupCompletion (group: PlatformSurvey[]): number | undefined {
  if (!group?.length) {
    return undefined
  }
  const totalCompletion = group.reduce((total, survey) => total + (calculateCompletion(survey) ?? 0), 0)
  return totalCompletion / group.length
}

export function getSurveyGroupCompletionDisplayVal (group: PlatformSurvey[]): string | undefined {
  const val = calculateSurveyGroupCompletion(group)
  if (val === undefined) {
    return undefined
  }
  return `${Math.round(val)}%`
}

/**
 * Check if all surveys in a group are finished (similar to logic in surveyUpdatedHandler)
 */
export function areAllSurveysFinished (surveys: PlatformSurvey[]): boolean {
  if (!surveys?.length) {
    return false
  }
  return surveys.every((survey: PlatformSurvey) => !!survey.finishedAt)
}

/**
 * Get the latest completion date from all finished surveys in a group
 */
export function getLatestCompletionDate (surveys: PlatformSurvey[]): string | undefined {
  if (!surveys?.length) {
    return undefined
  }

  const finishedSurveys = surveys.filter((survey: PlatformSurvey) => !!survey.finishedAt)
  if (!finishedSurveys.length) {
    return undefined
  }

  // Find the survey with the latest finishedAt date
  const latestSurvey = finishedSurveys.reduce((latest, current) => {
    const latestDate = new Date(latest.finishedAt as string)
    const currentDate = new Date(current.finishedAt as string)
    return currentDate > latestDate ? current : latest
  })

  return latestSurvey.finishedAt ?? undefined
}

/**
 * Get the start date from survey group creation date
 */
export function getEarliestStartDate (surveyGroup: PlatformSurveyGroup): string | undefined {
  if (!surveyGroup) {
    return undefined
  }

  // Use survey group createdAt as the start date
  return surveyGroup.createdAt
}

// Get survey group completion status with metadata

export function getSurveyGroupCompletionStatus (surveys: PlatformSurvey[]): {
  allFinished: boolean
  latestCompletionDate?: string
  completedCount: number
  totalCount: number
} {
  const allFinished = areAllSurveysFinished(surveys)
  const latestCompletionDate = getLatestCompletionDate(surveys)
  const completedCount = surveys.filter(survey => !!survey.finishedAt).length
  const totalCount = surveys.length

  return {
    allFinished,
    latestCompletionDate,
    completedCount,
    totalCount
  }
}

/**
 * Format completion date for display in evaluation information
 * Supports both English and German locales
 */
export function formatCompletionDateForEvaluation (dateString?: string, locale: string = 'en-US'): string {
  if (!dateString) {
    return 'N/A'
  }

  const date = new Date(dateString)
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }

  // Support German locale
  const supportedLocale = locale === 'de' || locale === 'de-DE' ? 'de-DE' : 'en-US'
  return date.toLocaleDateString(supportedLocale, options)
}

/**
 * Generate multilingual survey completion text using i18n translations
 * This function should be called from Vue components that have access to $t
 */
export function generateSurveyCompletionText (
  surveyGroup: PlatformSurveyGroup,
  locale: string,
  translateFn: (key: string, values?: any) => string
): string {
  const surveys = surveyGroup.surveys?.items || []
  const completionStatus = getSurveyGroupCompletionStatus(surveys)
  const startDate = getEarliestStartDate(surveyGroup)

  if (completionStatus.allFinished && completionStatus.latestCompletionDate) {
    const completionDate = formatCompletionDateForEvaluation(completionStatus.latestCompletionDate, locale)
    const formattedStartDate = startDate ? formatCompletionDateForEvaluation(startDate, locale) : '[start date day/month/year]'
    return translateFn('analysis.systemPages.evaluationInfo.completionTemplates.completed', {
      startDate: formattedStartDate,
      completionDate
    })
  } else {
    let text = translateFn('analysis.systemPages.evaluationInfo.completionTemplates.inProgress', {
      completedCount: completionStatus.completedCount,
      totalCount: completionStatus.totalCount
    })

    if (completionStatus.latestCompletionDate) {
      const latestDate = formatCompletionDateForEvaluation(completionStatus.latestCompletionDate, locale)
      text += translateFn('analysis.systemPages.evaluationInfo.completionTemplates.latestCompletion', {
        latestDate
      })
    }

    return text
  }
}

/**
 * Generate comparison date note text for evaluation information
 * Uses smart date formatting - shows time if same day as original, otherwise just date
 * This function should be called from Vue components that have access to $t
 */
export function generateComparisonDateNoteText (
  comparisonCreatedAt: string,
  originalCreatedAt: string,
  locale: string,
  translateFn: (key: string, values?: any) => string
): string {
  const comparisonDate = getDisplayComparisonDate(comparisonCreatedAt, originalCreatedAt, locale)
  return translateFn('report.evaluationInformation.comparisonDateNote', [comparisonDate])
}

export function SortSurveyGroups (a: SurveyGroupProgress, z: SurveyGroupProgress): number {
  if (a.companyId !== z.companyId) {
    return 0
  }
  if (!a.releasedAt) {
    return 1
  }
  if (!z.releasedAt) {
    return -1
  }
  return z.releasedAt.localeCompare(a.releasedAt)
}

/**
 * Sort function for a list of QuestionnaireListItems (Questionnaires or SurveyGroups)
 */
export function SortQuestionnairesSurveyGroups (a: QuestionnaireListItem | PlatformQuestionnaire, z: QuestionnaireListItem | PlatformQuestionnaire): number {
  if (a.companyId !== z.companyId) {
    return 0
  }

  const getTimestamp = (item: QuestionnaireListItem | PlatformQuestionnaire): string | undefined => {
    return 'releasedAt' in item ? item.releasedAt ?? item.createdAt : item.createdAt
  }

  const aTimestamp: string | undefined = getTimestamp(a)
  const zTimestamp: string | undefined = getTimestamp(z)

  if (!aTimestamp) {
    return 1
  }
  if (!zTimestamp) {
    return -1
  }
  return zTimestamp.localeCompare(aTimestamp)
}

export async function getSurveyGroupById (surveyGroupId: string): Promise<PlatformSurveyGroup | undefined> {
  let response: GraphQLResult<GetFullSurveyGroupQuery>
  try {
    response = await API.graphql({
      query: getFullSurveyGroup,
      variables: {
        id: surveyGroupId
      }
    }) as GraphQLResult<GetFullSurveyGroupQuery>
  } catch (err) {
    console.error('Failed to query Survey Group by ID.', JSON.stringify({ surveyGroupId, err }, null, 2))
    throw err
  }
  if (response.errors) {
    console.error('Got errors while querying SurveyGroup by ID:', response.errors)
    return undefined
  }
  return response.data?.getSurveyGroup as PlatformSurveyGroup | undefined
}

export async function getSurveyGroupByQuestionnaire (questionnaireId: string): Promise<PlatformSurveyGroup | undefined> {
  let response: GraphQLResult<ListFullSurveyGroupsByQuestionnaireQuery>
  try {
    response = await API.graphql({
      query: listFullSurveyGroupsByQuestionnaire,
      variables: {
        questionnaireId
      }
    }) as GraphQLResult<ListFullSurveyGroupsByQuestionnaireQuery>
  } catch (err) {
    console.error('Failed to list Survey Groups by Questionnaire.', JSON.stringify({ questionnaireId, err }, null, 2))
    throw err
  }
  if (response.errors) {
    console.error('Got errors while listing SurveyGroups by Questionnaire:', response.errors)
    return undefined
  }
  return response.data?.listSurveyGroupsByQuestionnaire?.items?.[0] as PlatformSurveyGroup | undefined
}

export async function getSurveyGroup (questionnaireId: string, surveyGroupId?: string | undefined): Promise<PlatformSurveyGroup | undefined> {
  if (surveyGroupId) {
    return await getSurveyGroupById(surveyGroupId)
  }
  return await getSurveyGroupByQuestionnaire(questionnaireId)
}
